/**
 * `ChatView` — the chat-screen container that wires the realtime
 * SignalR connection, REST history, read-receipt tracking, and visual
 * children (`ConnectionStatus`, `MessageList`, `MessageInput`) into a
 * single mountable surface.
 *
 * Responsibilities (requirements coverage R6.4, R9.4, R9.5):
 *   - Own the SignalR lifecycle for the active room. On mount, call
 *     `chatApi.negotiate` and pipe the resulting `{ url, accessToken }`
 *     into `client.connect`; on unmount, `useSignalRConnection` tears
 *     the connection down.
 *   - Source the transcript from `useMessages`, observe visibility via
 *     `useReadReceipt`, and surface the viewer's unread count in the
 *     header (R6.4).
 *   - Track the *other* participant's `lastReadSeq` as local state
 *     (seeded from `joinOutput.readReceipts` and advanced by SignalR's
 *     `readReceiptChanged` event) so own messages flip from "未読" to
 *     "既読" without a re-fetch (R6.3, owned by `MessageList`).
 *   - Provide a responsive shell (`.chat-view` / `.chat-view__container`)
 *     that the stylesheet positions full-bleed on mobile and centered
 *     with a comfortable `max-width` on desktop (R9.4).
 *   - Expose an optional leave affordance (`onLeave` prop). When the
 *     caller wires routing in task 7.1 this lets users return to the
 *     entry screen; tests can simulate it without dragging in the
 *     router.
 *   - Drive the `ConnectionStatus` banner so transient `connecting` /
 *     `reconnecting` / hard `failed` states are visible (R4.6 — owned
 *     by `ConnectionStatus`, but the parent injects the state).
 *
 * Dependency direction (design.md §Dependency Direction):
 *   This component sits at the top of the Frontend Layer and depends on
 *   `../api/chatApi`, `../api/signalRClient`, the three hooks
 *   (`useSignalRConnection`, `useMessages`, `useReadReceipt`), and the
 *   three sibling presentational components (`ConnectionStatus`,
 *   `MessageList`, `MessageInput`). It never reaches into `fetch` or
 *   the SignalR library directly — all such work happens behind the
 *   injected `ChatApi` and the `signalRClientFactory`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';

import { HamburgerMenu } from './HamburgerMenu';
import type { HamburgerMenuItem } from './HamburgerMenu';
import { TemplatePickerModal } from './TemplatePickerModal';

import type {
  ChatApi,
  NegotiateRequest,
  RoomJoinOutput,
} from '../api/chatApi';
import type { SignalRClient } from '../api/signalRClient';

import { useSignalRConnection } from '../hooks/useSignalRConnection';
import { useMessages } from '../hooks/useMessages';
import { useReadReceipt } from '../hooks/useReadReceipt';

import { ConnectionStatus } from './ConnectionStatus';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ChatViewProps {
  /** REST surface used for negotiate / list / send / markRead. */
  readonly chatApi: ChatApi;
  /**
   * Factory invoked exactly once to build the SignalR client. We accept a
   * factory (not an instance) so the hook can lazily create the client per
   * mount and so tests can inject a deterministic fake without forcing the
   * parent to hold a long-lived instance.
   */
  readonly signalRClientFactory: () => SignalRClient;
  /** Active room id (64-character SHA-256 hex). */
  readonly roomId: string;
  /** Viewer's own nickname. */
  readonly nickname: string;
  /**
   * Pre-computed SignalR userId used as the negotiate request `userId`
   * field. The negotiate handler verifies this value against
   * `${roomId}:${sha256Hex(nickname)}` and rejects mismatches, so the
   * parent (`App.tsx`) computes the hash once at join-time and passes the
   * canonical string down. Optional purely so call-sites in tests that
   * don't drive the negotiate path can omit it; production callers must
   * provide it. (See tasks.md Implementation Notes 6.1.)
   */
  readonly negotiateUserId?: string;
  /**
   * Server snapshot at the moment the viewer joined. Provides the initial
   * `participants`, `readReceipts`, and `lastSeq` so the transcript can
   * render unread counts before the first history page resolves.
   */
  readonly joinOutput: RoomJoinOutput;
  /**
   * Optional leave handler. When provided, the header renders a "退室"
   * button that invokes the callback on click. Routing decisions (e.g.,
   * returning to PasswordEntry) live in the parent — see task 7.1.
   */
  readonly onLeave?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pick the *other* participant's nickname from the join snapshot.
 *
 * Returns `null` when the viewer is currently alone in the room — the
 * caller treats that as "no one to receive read receipts from yet" and
 * leaves the read indicator at 0.
 *
 * Implementation note: `participants` is bounded to 2 by the server
 * (R3.1), so the search is a constant-time pass and we don't need a
 * Map. Filtering by !== nickname also handles the (illegal but
 * defensible) case where the array contains duplicate self entries.
 */
function pickOtherParticipant(
  participants: readonly string[],
  myNickname: string,
): string | null {
  for (const p of participants) {
    if (p !== myNickname) return p;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChatView(props: ChatViewProps): JSX.Element {
  const {
    chatApi,
    signalRClientFactory,
    roomId,
    nickname,
    negotiateUserId,
    joinOutput,
    onLeave,
  } = props;

  // -- Announcement state & listener --------------------------------------
  const [announcement, setAnnouncement] = useState<string | undefined>(joinOutput.announcement);

  // Fall back to a deterministic but unverified string when no
  // pre-computed userId is provided. The negotiate handler will reject
  // the request as `userIdMismatch`, but the chat view still renders so
  // existing history / composer behaviour stays observable. Tests that
  // don't drive the negotiate path also benefit from this fallback.
  const effectiveNegotiateUserId =
    negotiateUserId !== undefined ? negotiateUserId : `${roomId}:${nickname}`;

  // -- SignalR lifecycle -------------------------------------------------
  // `useSignalRConnection` owns the client. We pass the factory through
  // verbatim — its identity isn't stable across renders, but the hook
  // captures it in a ref on first mount so the second-render identity
  // doesn't matter. (Verified in `useSignalRConnection.test.ts`.)
  const { state, connect, client } = useSignalRConnection(signalRClientFactory);

  // Subscribe to announcementChanged SignalR events
  useEffect(() => {
    const unsubscribe = client.onAnnouncementChanged((event) => {
      if (event.roomId === roomId) {
        setAnnouncement(event.announcement);
      }
    });
    return () => {
      unsubscribe();
    };
  }, [client, roomId]);

  // -- Other-party read-receipt state -----------------------------------
  // Seeded from the join snapshot. Updated reactively when SignalR pushes
  // a `readReceiptChanged` event for the *other* participant. Self
  // receipts (`event.nickname === nickname`) are ignored here — the
  // viewer's own read indicator is driven by `useReadReceipt`, not by
  // observing themselves.
  const otherNickname = useMemo(
    () => pickOtherParticipant(joinOutput.participants, nickname),
    [joinOutput.participants, nickname],
  );

  const [otherReadReceipt, setOtherReadReceipt] = useState<number>(() => {
    if (otherNickname === null) return 0;
    return joinOutput.readReceipts[otherNickname] ?? 0;
  });

  // -- Template messages -------------------------------------------------
  // オーナーがダッシュボードで設定したテンプレートメッセージ一覧。
  // joinOutput からシードし、SignalR `templatesChanged` イベントで更新する。
  // ハンバーガーメニュー → モーダルから選択させ、選択結果は pendingInsert で
  // MessageInput に伝播する。
  const [templates, setTemplates] = useState<readonly string[]>(
    joinOutput.templates ?? [],
  );

  // テンプレート選択モーダルの開閉状態。
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  // MessageInput へのテンプレート挿入トリガ。同一テンプレートを連続選択した
  // 場合でも nonce が必ず変わるよう、選択ごとにインクリメントする。
  const [pendingTemplateInsert, setPendingTemplateInsert] = useState<{
    readonly text: string;
    readonly nonce: number;
  } | null>(null);

  useEffect(() => {
    const unsubscribe = client.onTemplatesChanged((event) => {
      if (event.roomId !== roomId) return;
      setTemplates(event.templates);
    });
    return () => {
      unsubscribe();
    };
  }, [client, roomId]);

  const handlePickTemplate = useCallback((tpl: string) => {
    setPendingTemplateInsert((prev) => ({
      text: tpl,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
    setTemplateModalOpen(false);
  }, []);

  // ハンバーガーメニューに渡す追加項目。テンプレートが 1 件以上あるときだけ
  // 表示する (空のメニューを開いても何も選べないため)。
  const hamburgerExtraItems = useMemo<readonly HamburgerMenuItem[]>(() => {
    if (templates.length === 0) return [];
    return [
      {
        icon: '📋',
        label: `テンプレート (${templates.length})`,
        onClick: () => setTemplateModalOpen(true),
        title: 'テンプレートメッセージを挿入',
      },
    ];
  }, [templates.length]);

  // -- Guest-message-limit state ----------------------------------------
  // The owner configures `guestMessageLimit` (0 = unlimited). Both sides
  // need the value to render the same UI affordances: the guest's composer
  // shows remaining quota; the owner sees a read-only summary. The counter
  // (`consecutiveGuestMessages`) is updated derivatively from `messages`
  // below so we don't need a dedicated SignalR event per send.
  const [guestMessageLimit, setGuestMessageLimit] = useState<number>(
    joinOutput.guestMessageLimit ?? 0,
  );
  const ownerNickname = joinOutput.ownerNickname;
  const isOwnerViewer =
    ownerNickname !== undefined && ownerNickname === nickname;

  // Limit + counter advance together on the dedicated SignalR event (set
  // up later, once the counter state is declared). The counter listener
  // is registered below in `-- Consecutive-guest-message counter`.

  // -- Other-party "show read receipt" preference -----------------------
  // Seeded from the join snapshot and kept live via the
  // `readReceiptSettingChanged` SignalR event. When this is `false`, the
  // MessageList suppresses the read/unread indicator on own messages — the
  // other participant has opted out of letting us see their reads, so
  // forever-"未読" badges would be misleading.
  const [otherShowReadReceipt, setOtherShowReadReceipt] = useState<boolean>(() => {
    if (otherNickname === null) return true;
    return joinOutput.participantShowReadReceipt?.[otherNickname] ?? true;
  });

  // Keep otherNickname reachable from the SignalR callback (it is closed
  // over the value at subscribe time otherwise). Same pattern as the
  // read-receipt listener above.
  const otherShowReceiptNicknameRef = useRef<string | null>(otherNickname);
  otherShowReceiptNicknameRef.current = otherNickname;

  useEffect(() => {
    const unsubscribe = client.onReadReceiptSettingChanged((event) => {
      const other = otherShowReceiptNicknameRef.current;
      if (other === null) return;
      // Only react to the OTHER participant's toggle — our own toggle is
      // applied locally through the Dashboard flow and the join snapshot.
      if (event.nickname !== other) return;
      if (event.roomId !== roomId) return;
      setOtherShowReadReceipt(event.showReadReceipt);
    });
    return () => {
      unsubscribe();
    };
  }, [client, roomId]);

  // Keep the latest "other nickname" reachable from the SignalR callback
  // without re-subscribing on every render. The callback is attached once
  // per `client` identity (stable for the hook lifetime), so we cannot
  // close over `otherNickname` directly.
  const otherNicknameRef = useRef<string | null>(otherNickname);
  otherNicknameRef.current = otherNickname;

  useEffect(() => {
    const unsubscribe = client.onReadReceiptChanged((event) => {
      // Self receipts are no-ops at this layer. The local `useReadReceipt`
      // hook owns the viewer's own read state; the server still emits the
      // event to the *other* user, but it can also echo back via the
      // group depending on the backend's targeting. Be defensive.
      const other = otherNicknameRef.current;
      if (other === null) return;
      if (event.nickname !== other) return;

      // Monotonic non-decreasing: the server is the same, but a delayed
      // push from a stale window mustn't move the indicator backwards.
      setOtherReadReceipt((prev) => (event.lastReadSeq > prev ? event.lastReadSeq : prev));
    });
    return () => {
      unsubscribe();
    };
  }, [client]);

  // -- Connect on mount --------------------------------------------------
  // The negotiate handshake + connect call runs exactly once per mount.
  // `connect` from `useSignalRConnection` is wrapped in `useCallback`
  // with the client identity (stable), so we can list it in deps without
  // re-firing.
  //
  // Why eager instead of waiting for the user to interact? Realtime is
  // table stakes for the chat screen — we don't want the user to type a
  // message into a disconnected client and watch it fail. We let the
  // `ConnectionStatus` banner surface the in-flight state.
  const negotiateAttemptedRef = useRef(false);
  useEffect(() => {
    // StrictMode double-mounts effects in dev; the ref guards the
    // negotiate fetch so we don't double-spend the rate-limit budget.
    if (negotiateAttemptedRef.current) return;
    negotiateAttemptedRef.current = true;

    let cancelled = false;
    (async () => {
      const request: NegotiateRequest = {
        roomId,
        nickname,
        userId: effectiveNegotiateUserId,
      };
      const result = await chatApi.negotiate(request);
      if (cancelled) return;

      if (!result.ok) {
        // Surface diagnostics; the ConnectionStatus banner will eventually
        // reflect a `failed` state when the client itself observes the
        // lack of a connection (we never invoke `connect`). We don't
        // crash the view — the user can read existing history and the
        // composer will still produce a user-visible error on send.
        console.warn('[ChatView] negotiate failed', result.error);
        return;
      }

      try {
        await connect({
          url: result.value.url,
          accessToken: result.value.accessToken,
        });
      } catch (cause) {
        // `client.connect` rejects only when `start()` fails outright;
        // the client also flips state to `failed` so `ConnectionStatus`
        // already surfaces this. Logging keeps the diagnostic trail.
        console.warn('[ChatView] signalR connect failed', cause);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `chatApi`, `roomId`, `nickname`, `effectiveNegotiateUserId` and
    // `connect` are all expected to be stable for the lifetime of this
    // view (it's mounted once per join). We intentionally exclude
    // `connect` from deps because the ref-guarded one-shot is the right
    // semantics; including it would re-run if the hook ever rebuilds the
    // callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatApi, roomId, nickname, effectiveNegotiateUserId]);

  // -- Message history + realtime ----------------------------------------
  const {
    messages,
    hasMore,
    isLoading,
    loadOlder,
    refresh,
    isRefreshing,
    sendMessage,
    resend,
  } = useMessages({
    chatApi,
    signalR: client,
    roomId,
    myNickname: nickname,
  });

  // -- Read-receipt observer --------------------------------------------
  const { observe } = useReadReceipt({
    chatApi,
    roomId,
    myNickname: nickname,
  });

  // -- Consecutive-guest-message counter --------------------------------
  // Seeded from the server-supplied snapshot at join time. We keep this
  // in local state and advance it on every realtime `newMessage` (own or
  // peer) so the composer's blocked / remaining display stays in sync
  // without polling the server. Owner messages reset the counter; guest
  // messages increment it.
  const [consecutiveGuestMessages, setConsecutiveGuestMessages] = useState<number>(
    joinOutput.consecutiveGuestMessages ?? 0,
  );

  // Reset to the server-provided counter whenever the limit changes (the
  // server resets the counter alongside the limit change). The dedicated
  // SignalR event already mutates limit + counter together.
  useEffect(() => {
    const unsubscribe = client.onGuestMessageLimitChanged((event) => {
      if (event.roomId !== roomId) return;
      setGuestMessageLimit(event.guestMessageLimit);
      setConsecutiveGuestMessages(event.consecutiveGuestMessages);
    });
    return () => {
      unsubscribe();
    };
  }, [client, roomId]);

  // Track per-message sender to advance the counter on every realtime
  // `newMessage`. We capture the current `ownerNickname` in a ref so the
  // subscription doesn't need to re-bind on each prop change.
  const ownerNicknameRef = useRef<string | undefined>(ownerNickname);
  ownerNicknameRef.current = ownerNickname;

  useEffect(() => {
    const unsubscribe = client.onNewMessage((msg) => {
      if (msg.roomId !== roomId) return;
      const owner = ownerNicknameRef.current;
      if (owner === undefined) return;
      setConsecutiveGuestMessages((prev) =>
        msg.senderNickname === owner ? 0 : prev + 1,
      );
    });
    return () => {
      unsubscribe();
    };
  }, [client, roomId]);

  const guestLimitReached =
    !isOwnerViewer &&
    ownerNickname !== undefined &&
    guestMessageLimit > 0 &&
    consecutiveGuestMessages >= guestMessageLimit;

  const guestRemaining =
    !isOwnerViewer && ownerNickname !== undefined && guestMessageLimit > 0
      ? Math.max(0, guestMessageLimit - consecutiveGuestMessages)
      : null;

  // -- Retry handler for the failed banner ------------------------------
  // When the auto-reconnect policy exhausts, `ConnectionStatus` shows a
  // retry button. We re-run the negotiate + connect dance so the user
  // doesn't have to reload the page.
  const handleRetry = useCallback(() => {
    negotiateAttemptedRef.current = false;
    // Trigger a fresh attempt by mutating a tick state. We use a number
    // counter instead of toggling a boolean so the effect dependency is
    // strictly increasing.
    setRetryTick((t) => t + 1);
  }, []);

  const [retryTick, setRetryTick] = useState(0);
  useEffect(() => {
    if (retryTick === 0) return;
    // Drop the guard so the negotiate effect can re-fire on the next
    // tick. We can't directly re-invoke the effect body because it's
    // closed over its own dep array; the simplest approach is to
    // duplicate the body here. We keep this short and ASCII-equivalent
    // to the mount effect.
    let cancelled = false;
    (async () => {
      const request: NegotiateRequest = {
        roomId,
        nickname,
        userId: effectiveNegotiateUserId,
      };
      const result = await chatApi.negotiate(request);
      if (cancelled) return;
      if (!result.ok) {
        console.warn('[ChatView] negotiate retry failed', result.error);
        return;
      }
      try {
        await connect({
          url: result.value.url,
          accessToken: result.value.accessToken,
        });
      } catch (cause) {
        console.warn('[ChatView] signalR connect retry failed', cause);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `connect` excluded — see mount effect rationale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryTick, chatApi, roomId, nickname, effectiveNegotiateUserId]);

  // -- Render ------------------------------------------------------------
  // Layout:
  //   <main.chat-view>
  //     <div.chat-view__container>
  //       <header.chat-view__header>
  //         <h1>二人だけのチャット</h1>
  //         <button>退室</button> (only when onLeave provided)
  //       </header>
  //       <ConnectionStatus state={state} onRetry={handleRetry} />
  //       <MessageList ... />
  //       <MessageInput ... />
  //     </div>
  //   </main>
  const displayedOther = otherNickname !== null ? otherNickname : nickname;
  // ヘッダタイトル: 「(相手) と (自分) のチャットルーム」。相手が未入室で
  // displayedOther が自分自身に fallback している場合は、自分の名前が
  // 2 回並ばないよう「（あなた）」だけにする (LINE のような自分単独表示)。
  const headerTitle =
    otherNickname !== null
      ? `${displayedOther}と${nickname}のチャットルーム`
      : `${nickname}のチャットルーム`;

  return (
    <div className="theme-stage">
      <span className="theme-blob theme-blob--a" aria-hidden="true" />
      <span className="theme-blob theme-blob--b" aria-hidden="true" />
      <span className="theme-blob theme-blob--c" aria-hidden="true" />

      <main className="chat-view theme-stage__content" aria-label="チャット画面">
        <div className="chat-view__container">
          <header className="chat-view__header" role="banner">
            <HamburgerMenu extraItems={hamburgerExtraItems} />
            {onLeave !== undefined && (
              <button
                type="button"
                className="chat-view__back"
                onClick={onLeave}
                aria-label="戻る"
              >
                ‹
              </button>
            )}
            <div className="chat-view__name-block">
              <h1 className="chat-view__title">{headerTitle}</h1>
            </div>
            <div className="chat-view__meta">
              <button
                type="button"
                className={
                  isRefreshing
                    ? 'chat-view__refresh chat-view__refresh--loading'
                    : 'chat-view__refresh'
                }
                onClick={() => {
                  void refresh();
                }}
                disabled={isRefreshing || isLoading}
                aria-label="メッセージを再読み込み"
                title="再読み込み"
              >
                <svg
                  className="chat-view__refresh-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    fill="currentColor"
                    d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 0 16c3.73 0 6.84-2.55 7.73-6h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
                  />
                </svg>
              </button>
              {onLeave !== undefined ? (
                <button
                  type="button"
                  className="chat-view__leave"
                  onClick={onLeave}
                  aria-label="ルームを退室"
                >
                  退室
                </button>
              ) : null}
            </div>
          </header>

          <ConnectionStatus state={state} onRetry={handleRetry} />

          {announcement && (
            <div className="chat-view__announcement-wrap" role="status">
              <div className="chat-view__announcement">
                <div className="chat-view__announcement-icon" aria-hidden="true">📢</div>
                <div className="chat-view__announcement-text">
                  <div className="chat-view__announcement-eyebrow">
                    📌 OWNER&apos;S NOTE
                  </div>
                  <div className="chat-view__announcement-body">{announcement}</div>
                </div>
              </div>
            </div>
          )}

          <MessageList
            messages={messages}
            myNickname={nickname}
            otherReadReceipt={otherReadReceipt}
            otherShowReadReceipt={otherShowReadReceipt}
            hasMore={hasMore}
            isLoadingOlder={isLoading}
            isInitialLoading={isLoading}
            onLoadOlder={() => {
              void loadOlder();
            }}
            onResend={(tempId) => {
              void resend(tempId);
            }}
            observeForRead={observe}
          />

          <MessageInput
            onSend={(content) => {
              void sendMessage(content);
            }}
            ownerNickname={ownerNickname}
            pendingInsert={pendingTemplateInsert}
            blockedReason={
              guestLimitReached
                ? `${ownerNickname ?? 'オーナー'}さんからの返信を待っています。連続送信は ${guestMessageLimit} 件までです。`
                : null
            }
            quotaHint={
              guestRemaining !== null && !guestLimitReached
                ? `あと ${guestRemaining} 件送信できます（${ownerNickname ?? 'オーナー'}さん返信までの上限 ${guestMessageLimit} 件）`
                : null
            }
          />
        </div>
      </main>

      {/* テンプレート選択モーダル。ハンバーガーメニューから開く。 */}
      {templateModalOpen && (
        <TemplatePickerModal
          templates={templates}
          onSelect={handlePickTemplate}
          onClose={() => setTemplateModalOpen(false)}
        />
      )}
    </div>
  );
}
